import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Ava } from "../../atoms/Ava.jsx";
import { SentDot } from "../../atoms/SentDot.jsx";

// Markdown component overrides styled for the dark chat theme
function mdComponents(pc) {
  return {
    p:      ({children}) => <p style={{margin:"0 0 6px",color:"#e2e8f0",fontSize:14,lineHeight:1.7,wordBreak:"break-word"}}>{children}</p>,
    strong: ({children}) => <strong style={{color:"#fff",fontWeight:700}}>{children}</strong>,
    em:     ({children}) => <em style={{color:"#cbd5e1",fontStyle:"italic"}}>{children}</em>,
    h1:     ({children}) => <h3 style={{color:"#f1f5f9",fontSize:15,fontWeight:800,margin:"8px 0 4px",fontFamily:"Georgia,serif"}}>{children}</h3>,
    h2:     ({children}) => <h3 style={{color:"#f1f5f9",fontSize:15,fontWeight:800,margin:"8px 0 4px",fontFamily:"Georgia,serif"}}>{children}</h3>,
    h3:     ({children}) => <h4 style={{color:"#f1f5f9",fontSize:14,fontWeight:800,margin:"6px 0 4px"}}>{children}</h4>,
    ul:     ({children}) => <ul style={{paddingLeft:18,margin:"4px 0 6px",color:"#e2e8f0",fontSize:14,lineHeight:1.7}}>{children}</ul>,
    ol:     ({children}) => <ol style={{paddingLeft:18,margin:"4px 0 6px",color:"#e2e8f0",fontSize:14,lineHeight:1.7}}>{children}</ol>,
    li:     ({children}) => <li style={{marginBottom:3,wordBreak:"break-word"}}>{children}</li>,
    code:   ({children}) => <code style={{background:"#1e2d45",borderRadius:4,padding:"2px 6px",fontFamily:"monospace",fontSize:13,color:"#93c5fd"}}>{children}</code>,
    a:      ({href,children}) => <a href={href} target="_blank" rel="noopener noreferrer" style={{color:"#60a5fa",textDecoration:"underline"}}>{children}</a>,
    blockquote: ({children}) => <blockquote style={{borderLeft:`3px solid ${pc}`,paddingLeft:10,margin:"6px 0",color:"#94a3b8",fontStyle:"italic"}}>{children}</blockquote>,
  };
}

/**
 * Flag reason modal — shown when voter clicks the flag button.
 * Collects reason: "Totally false", "Unverifiable", or "Other" (free text).
 */
function FlagReasonModal({ onSubmit, onClose, pc }) {
  const [reason, setReason] = useState("");
  const [otherText, setOtherText] = useState("");

  const submit = () => {
    const flagReason = reason === "other" ? otherText.trim() : reason;
    if (!flagReason) return;
    onSubmit(flagReason);
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.65)"}} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{width:"100%",maxWidth:340,margin:"0 18px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:14,padding:20,animation:"fadeUp .2s ease"}}>
        <h4 style={{margin:"0 0 4px",color:"#f1f5f9",fontSize:15,fontWeight:800,fontFamily:"Georgia,serif"}}>⚠️ Flag this response</h4>
        <p style={{color:"#64748b",fontSize:12,margin:"0 0 16px"}}>Why are you flagging this response?</p>

        {[["Totally false", "false"], ["Unverifiable", "unverifiable"]].map(([label, value]) => (
          <button key={value} onClick={() => setReason(value)}
            style={{display:"block",width:"100%",padding:"10px 14px",marginBottom:8,background:reason===value?`${pc}18`:"#090e1a",border:`1px solid ${reason===value?pc+"50":"#1e2d45"}`,borderRadius:9,color:reason===value?pc:"#94a3b8",fontSize:13,cursor:"pointer",fontFamily:"inherit",textAlign:"left",fontWeight:reason===value?700:400}}>
            {label}
          </button>
        ))}

        <button onClick={() => setReason("other")}
          style={{display:"block",width:"100%",padding:"10px 14px",marginBottom:8,background:reason==="other"?`${pc}18`:"#090e1a",border:`1px solid ${reason==="other"?pc+"50":"#1e2d45"}`,borderRadius:9,color:reason==="other"?pc:"#94a3b8",fontSize:13,cursor:"pointer",fontFamily:"inherit",textAlign:"left",fontWeight:reason==="other"?700:400}}>
          Other
        </button>

        {reason === "other" && (
          <textarea value={otherText} onChange={e => setOtherText(e.target.value.slice(0, 200))}
            placeholder="Describe the issue (max 200 chars)..."
            style={{width:"100%",padding:"10px 12px",background:"#090e1a",border:"1px solid #1e2d45",borderRadius:9,color:"#e2e8f0",fontSize:13,fontFamily:"inherit",resize:"vertical",minHeight:60,maxHeight:120,marginBottom:8,outline:"none",boxSizing:"border-box"}}/>
        )}

        <div style={{display:"flex",gap:8,marginTop:4}}>
          <button onClick={onClose} style={{flex:1,padding:"10px",background:"#090e1a",border:"1px solid #1e2d45",borderRadius:9,color:"#64748b",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          <button onClick={submit} disabled={!reason || (reason === "other" && !otherText.trim())}
            style={{flex:2,padding:"10px",background:reason?"linear-gradient(135deg,#d97706,#f59e0b)":"#1e2d45",border:"none",borderRadius:9,color:reason?"#fff":"#475569",fontSize:13,cursor:reason?"pointer":"not-allowed",fontFamily:"inherit",fontWeight:700}}>
            Submit Flag
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Renders a single chat message bubble (user or assistant).
 * CHAT-2: Feedback is persisted, locked after submission, flag has reason modal.
 * CHAT-3: Sources shown with numbered citations matching inline [1], [2] references.
 */
export function ChatMessageBubble({ m, persona, pc, deepDiveBusy, showTellMore, displayText, isNew, onDeepDive, onFeedback }) {
  const [showFlagModal, setShowFlagModal] = useState(false);
  const isLocked = m.feedbackLocked || false;

  const handleFeedback = (type) => {
    if (isLocked) return;
    if (type === "flagged") {
      setShowFlagModal(true);
      return;
    }
    onFeedback(m.id, type);
  };

  const handleFlagSubmit = (flagReason) => {
    setShowFlagModal(false);
    onFeedback(m.id, "flagged", flagReason);
  };

  const feedbackButtons = [
    { type: "liked", label: "👍", activeColor: "#16a34a", activeBg: "#16a34a22" },
    { type: "disliked", label: "👎", activeColor: "#dc2626", activeBg: "#dc262622" },
    { type: "flagged", label: "⚠️ Flag", activeColor: "#d97706", activeBg: "#d9770622" },
  ];

  return (
    <div style={{
      display: "flex", gap: 8,
      flexDirection: m.role === "user" ? "row-reverse" : "row",
      alignItems: "flex-end",
      animation: isNew ? "msgFadeIn .3s ease" : "none",
    }}>
      {m.role === "assistant" && <Ava ini={persona.avatar} color={pc} size={30} photoUrl={persona.photoUrl} />}
      <div style={{ maxWidth: "80%", minWidth: 0 }}>
        {/* Deep dive badge */}
        {m.isDeepDive && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", background: `${pc}18`, border: `1px solid ${pc}40`, borderRadius: 20, fontSize: 10, color: pc, fontWeight: 700, marginBottom: 5 }}>
            🔍 Deep Dive
          </div>
        )}

        <div style={{ padding: "11px 14px", borderRadius: m.role === "user" ? "16px 16px 4px 16px" : m.isDeepDive ? "12px" : "16px 16px 16px 4px", background: m.role === "user" ? `linear-gradient(135deg,${pc},${pc}bb)` : m.isDeepDive ? `${pc}0d` : "#0d1724", border: m.role === "assistant" ? `1px solid ${m.isDeepDive ? pc + "30" : "#1e2d45"}` : "none", wordBreak: "break-word" }}>
          {m.role === "user" ? (
            <span style={{ color: "#fff", fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{displayText}</span>
          ) : (
            <div style={{ color: "#e2e8f0" }}>
              <ReactMarkdown components={mdComponents(pc)}>{displayText}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Read more links — KB source references */}
        {m.sources?.length > 0 && (
          <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 3 }}>
            <div style={{ color: "#475569", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 1 }}>Read more</div>
            {m.sources.map((s, i) => (
              s.url ? (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 8, color: pc, fontSize: 12, textDecoration: "none", lineHeight: 1.4, wordBreak: "break-word" }}>
                  <span style={{ fontSize: 11, flexShrink: 0 }}>🔗</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}>{s.title}</span>
                </a>
              ) : (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 8, color: "#94a3b8", fontSize: 12, lineHeight: 1.4 }}>
                  <span style={{ fontSize: 11, flexShrink: 0 }}>📄</span>{s.title}
                </div>
              )
            ))}
          </div>
        )}

        {/* Tell me more button */}
        {showTellMore && (
          <button onClick={() => onDeepDive(m)}
            style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6, padding: "5px 12px", background: `${pc}15`, border: `1px solid ${pc}40`, borderRadius: 20, color: pc, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            🔍 Tell me more →
          </button>
        )}
        {deepDiveBusy === m.id && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6, padding: "5px 12px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 20, color: "#64748b", fontSize: 12 }}>
            <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span> Loading deep dive…
          </div>
        )}

        {/* Sentiment dot + feedback buttons (CHAT-2: locked after submission) */}
        <div style={{ display: "flex", gap: 5, marginTop: 5, alignItems: "center", flexDirection: m.role === "user" ? "row-reverse" : "row", flexWrap: "wrap" }}>
          {m.role === "user" && <SentDot s={m.sentiment} />}
          {m.role === "assistant" && feedbackButtons.map(({ type, label, activeColor, activeBg }) => {
            const isActive = m.feedback === type;
            const isDisabled = isLocked && !isActive;
            return (
              <button key={type} onClick={() => handleFeedback(type)}
                disabled={isDisabled}
                style={{
                  padding: "3px 9px",
                  background: isActive ? activeBg : "transparent",
                  border: `1px solid ${isActive ? activeColor : "#1e2d45"}`,
                  borderRadius: 20,
                  color: isActive ? activeColor : isDisabled ? "#334155" : "#475569",
                  fontSize: 11,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  opacity: isDisabled ? 0.4 : 1,
                  transition: "opacity .2s",
                }}>{label}</button>
            );
          })}
        </div>
      </div>

      {showFlagModal && (
        <FlagReasonModal
          onSubmit={handleFlagSubmit}
          onClose={() => setShowFlagModal(false)}
          pc={pc}
        />
      )}
    </div>
  );
}

export default ChatMessageBubble;
